import React, { lazy, Suspense } from 'react';
import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import PageLoading from '../PageLoading';
import { AccountDelete } from '../pages/DELETE/AccountDelete';
import DesktopAuthLayout from '../auth/DesktopAuthLayout';

import MobileDesignLayout from "../mobile/mobileLayout/MobileDesignLayout";
import MobileHomePage from "../mobile/mobilePages/mobileHomePage/MobileHomePage";
import MobileHomeProductDetails from "../mobile/mobilePages/mobileHomePage/MobileHomeProductDetails";
import MobileSearch from "../mobile/mobilePages/MobileSearch";
import MobileAllProducts from "../mobile/mobilePages/MobileAllProducts";
import MobileNotifications from "../mobile/mobilePages/MobileNotications";
import MobileProfile from "../mobile/mobilePages/MobileProfile";
import MobileContactUs from "../mobile/mobilePages/MobileContactUs";
import MobileOrders from "../mobile/mobilePages/MobileOrders";
import MobileUserTransactions from "../mobile/mobilePages/MobileUserTransactions";
import MobileAuthLayout from "../auth/MobileAuthLayout";

// Redirect to app store
const MobileRedirect = lazy(() => import('../components/MobileRedirect/MobileRedirect'));

// Lazy-load components for public routes
const ProfileLayout = lazy(() => import('../layouts/ProfileLayout'));

const BlogDetail = lazy(() => import('../pages/BLOGDETAILS'));
const CorpPage = lazy(() => import('../pages/CORP'));
const PageNotFound = lazy(() => import('../pages/OTHERPAGE/PageNotFound'));
const Thankyou = lazy(() => import('../pages/OTHERPAGE/Thankyou'));
const Layouts = lazy(() => import('../layouts'));
const AuthenticateUser = lazy(() => import('./AuthticateUser'));

// Lazy-load components for user routes
const HomePage = lazy(() => import('../pages/DASHBOARD/Homepage'));
const AllGiftCardPage = lazy(() => import('../pages/ALLGIFTCARD/AllGiftCardPage'));
const ProductDetailpage = lazy(() => import('../pages/PRODUCTDETAILS'));
const AboutPage = lazy(() => import('../pages/ABOUTUS'));
const ContactPage = lazy(() => import('../pages/CONTACTUS'));
const BlogPage = lazy(() => import('../pages/BLOGS'));
const GiftCardWorks = lazy(() => import('../pages/GIFTCARDSWORKS'));
const PrivacyPolicy = lazy(() => import('../pages/PRIVACYPOLICY'));
const CorpPolicy = lazy(() => import('../pages/CORPPOLICY'));
const TermsAndCondition = lazy(() => import('../pages/TERMSANDCONDITION'));

// Lazy-load components for admin routes
const Statement = lazy(() => import('../pages/profileDashboard/statement'));
const UserProfile = lazy(() => import('../pages/profileDashboard/user'));
const Order = lazy(() => import('../pages/profileDashboard/order'));
// const Notification = lazy(() => import('../pages/profileDashboard/notification'));
const ChangePassword = lazy(() => import('../pages/profileDashboard/changePassword'));
const ViewCards = lazy(() => import('../pages/profileDashboard/viewCard'));

const basename = process.env.REACT_APP_ENDPOINT || '/';

export const router = createBrowserRouter(
    createRoutesFromElements(
        <React.Fragment>
            <Route path="/" element={<Layouts />}>
                <Route index element={<HomePage />} />
                <Route path="card-list/:category?/*?" element={<AllGiftCardPage />} />
                <Route path="card-details/:id/:title?" element={<ProductDetailpage />} />
                <Route path="aboutus" element={<AboutPage />} />
                <Route path="contactus" element={<ContactPage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="blog-details/:title" element={<BlogDetail />} />

                <Route path="how-gift-card-work" element={<GiftCardWorks />} />
                <Route path="corppolicy" element={<CorpPolicy />} />
                <Route path="privacypolicy" element={<PrivacyPolicy />} />
                <Route path="termsandcondition" element={<TermsAndCondition />} />
                <Route path="corporates" element={<CorpPage />} />
                <Route path="thank-you" element={<Thankyou />} />
                <Route path="*" element={<Navigate to="/page-not-found" replace />} />
                <Route path="page-not-found" element={<PageNotFound />} />
            </Route>

            <Route path="/" element={<AuthenticateUser />}>
                <Route path="login" index element={<DesktopAuthLayout />} />
                <Route path="signup" element={<DesktopAuthLayout />} />
                <Route path="password-reset" element={<DesktopAuthLayout />} />
            </Route>

            <Route
                path="/profile/*"
                element={
                    <Suspense fallback={<PageLoading />}>
                        <ProfileLayout />
                    </Suspense>
                }
            >
                <Route index element={<Navigate to="/profile/order" />} />
                <Route path="order">
                    <Route index element={<Order />} />
                    <Route path=":orderId" element={<ViewCards />} />
                </Route>
                <Route path="statement" element={<Statement />} />
                <Route path="user-profile" element={<UserProfile />} />
                {/* <Route path="notifications" element={<Notification />} /> */}
                <Route path="password-change" element={<ChangePassword />} />
                <Route path="account-delete" element={<AccountDelete />} />
                <Route path="*" element={<Navigate to="/profile/order" replace />} />
            </Route>

            <Route path="mobile-redirect" element={<MobileRedirect />} />
        </React.Fragment>
    ),
    {
        basename,
        future: {
            v7_startTransition: true,
        },
    }
);

export const mobileRouter = createBrowserRouter(
    createRoutesFromElements(
        <React.Fragment>
            {/* Main layout for mobile */}
            <Route path="/" element={<MobileDesignLayout />}>
                <Route index element={<MobileHomePage />} />
                <Route path="card-list/:category?/*?" element={<MobileAllProducts />} />
                <Route path="card-details/:id/:title?" element={<MobileHomeProductDetails />} />
                <Route path="search" element={<MobileSearch />} />

                <Route path="contact-us" element={<Navigate to="/contactus" replace />} />
                <Route path="contactus" element={<MobileContactUs />} />

                <Route path="aboutus" element={<AboutPage isMobile={true} />} />
                <Route path="blog" element={<BlogPage isMobile={true} />} />
                <Route path="blog-details/:title" element={<BlogDetail isMobile={true} />} />
                <Route path="how-gift-card-work" element={<GiftCardWorks isMobile={true} />} />
                <Route path="privacypolicy" element={<PrivacyPolicy isMobile={true} />} />
                <Route path="corppolicy" element={<CorpPolicy isMobile={true} />} />
                <Route path="termsandcondition" element={<TermsAndCondition isMobile={true} />} />
                <Route path="corporates" element={<CorpPage />} />

                <Route path="thank-you" element={<Thankyou />} />
                <Route path="*" element={<Navigate to="/page-not-found" replace />} />
                <Route path="page-not-found" element={<PageNotFound />} />
            </Route>

            {/* Profile layout */}
            <Route
                path="/profile/*"
                element={
                    <Suspense fallback={<PageLoading />}>
                        <ProfileLayout />
                    </Suspense>
                }
            >
                <Route index element={<MobileProfile />} />
                <Route path="order" element={<MobileOrders />} />
                <Route path="statement" element={<MobileUserTransactions />} />
                <Route path="account-delete" element={<AccountDelete />} />
                <Route path="*" element={<Navigate to="/profile" replace />} />
            </Route>

            <Route path="/"
                element={
                    <Suspense fallback={<PageLoading />}>
                        <AuthenticateUser />
                    </Suspense>
                }
            >
                <Route path="login" element={<MobileAuthLayout />} />
                <Route path="signup" element={<MobileAuthLayout />} />
                <Route path="password-reset" element={<MobileAuthLayout />} />
            </Route>

            <Route path="*" element={<PageNotFound />} />
        </React.Fragment>
    ),
    {
        basename,
        future: {
            v7_startTransition: true,
        },
    }
);

